import React from "react";
import Alert from "@cloudscape-design/components/alert";
import Badge from "@cloudscape-design/components/badge";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import ColumnLayout from "@cloudscape-design/components/column-layout";
import Container from "@cloudscape-design/components/container";
import Header from "@cloudscape-design/components/header";
import Modal from "@cloudscape-design/components/modal";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Tabs from "@cloudscape-design/components/tabs";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { createDefaultNotificationsRepeatModal, notificationsRepeatModalStore } from "../stores/notificationsRepeatModal";
import CohortConditionsEditor from "./CohortConditionsEditor";

const formatDateTime = (value) => {
	if (!value) return "-";
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString();
};

const formatNumber = (value) => (typeof value === "number" ? value.toLocaleString() : "-");

function NotificationRepeatModal({ Interface }) {
	const modal = useRecoilValue(notificationsRepeatModalStore);
	const setModal = useSetRecoilState(notificationsRepeatModalStore);
	const requestIdRef = React.useRef(0);
	const notification = modal.data;
	const preview = modal.audiencePreview;

	const closeModal = React.useCallback(() => {
		requestIdRef.current += 1;
		setModal(createDefaultNotificationsRepeatModal());
	}, [setModal]);

	const previewAudience = React.useCallback(
		async (cohortConditions) => {
			if (!notification?.id) return;
			const conditions = cohortConditions || [];
			const requestId = ++requestIdRef.current;

			setModal((prev) => ({
				...prev,
				cohortConditions: conditions,
				audiencePreview: { ...prev.audiencePreview, loading: true, error: "" },
			}));

			try {
				const response = await Interface.request(
					"get",
					{ class: "data", module: "notification", method: "previewRepeat" },
					{ id: notification.id, cohortConditions: conditions },
				);
				if (requestId !== requestIdRef.current) return;

				setModal((prev) => ({
					...prev,
					cohortConditions: response?.cohortConditions || conditions,
					audiencePreview: {
						loading: false,
						count: Number(response?.count || 0),
						sentUsers: Number(response?.sentUsers || 0),
						expiredUsers: Number(response?.expiredUsers || 0),
						readUsers: Number(response?.readUsers || 0),
						queuedUsers: Number(response?.queuedUsers || 0),
						error: "",
					},
				}));
			} catch (error) {
				if (requestId !== requestIdRef.current) return;
				setModal((prev) => ({
					...prev,
					audiencePreview: {
						...prev.audiencePreview,
						loading: false,
						count: null,
						error: error?.text || error?.toString?.() || "Failed to preview repeat audience",
					},
				}));
			}
		},
		[Interface, notification?.id, setModal],
	);

	React.useEffect(() => {
		if (modal.show && notification?.id) previewAudience(modal.cohortConditions || []);
	}, [modal.show, notification?.id]);

	const updateCohortConditions = (cohortConditions) => {
		requestIdRef.current += 1;
		setModal((prev) => ({
			...prev,
			cohortConditions,
			audiencePreview: {
				loading: false,
				count: null,
				sentUsers: null,
				expiredUsers: null,
				readUsers: null,
				queuedUsers: null,
				error: "",
			},
		}));
	};

	const repeatNotification = async () => {
		if (!notification?.id || typeof preview.count !== "number" || preview.count <= 0) return;
		setModal((prev) => ({ ...prev, loaded: false }));

		try {
			const response = await Interface.request(
				"set",
				{ class: "data", module: "notification", method: "repeat" },
				{ id: notification.id, cohortConditions: modal.cohortConditions || [] },
			);
				const audienceCount = Number(response?.audienceCount ?? preview.count ?? response?.repeated ?? 0);
				Interface.addNoty(
					"Success",
					response?.queued
						? `${audienceCount.toLocaleString()} users queued for repeat notification`
						: "No users are currently eligible for repeat notification"
				);
			closeModal();
		} catch (error) {
			Interface.addNoty("Error", error?.text || error?.toString?.() || "Failed to repeat notification");
			setModal((prev) => ({ ...prev, loaded: true }));
		}
	};

	const repeatDisabled = !modal.loaded || preview.loading || typeof preview.count !== "number" || preview.count <= 0;

	return (
		<Modal
			visible={modal.show}
			onDismiss={closeModal}
			header={notification ? `Repeat Notification #${notification.id}` : "Repeat Notification"}
			size="large"
			footer={
				<Box float="right">
					<SpaceBetween direction="horizontal" size="xs">
						<Button onClick={closeModal}>Close</Button>
						<Button variant="primary" loading={!modal.loaded} disabled={repeatDisabled} onClick={repeatNotification}>
							Repeat
						</Button>
					</SpaceBetween>
				</Box>
			}
		>
			{notification ? (
				<Tabs
					tabs={[
						{
							id: "notification",
							label: "Notification",
							content: (
								<SpaceBetween size="m">
									{preview.error ? <Alert type="error">{preview.error}</Alert> : null}
									<Container
										header={
											<Header actions={<Button loading={preview.loading} onClick={() => previewAudience(modal.cohortConditions || [])}>Refresh audience</Button>}>
												Repeat audience
											</Header>
										}
									>
										<ColumnLayout columns={5} variant="text-grid">
											<div><Box variant="awsui-key-label">Will be repeated</Box><Box variant="h2">{formatNumber(preview.count)}</Box></div>
											<div><Box variant="awsui-key-label">Sent</Box><Box variant="h2">{formatNumber(preview.sentUsers)}</Box></div>
											<div><Box variant="awsui-key-label">Expired</Box><Box variant="h2">{formatNumber(preview.expiredUsers)}</Box></div>
											<div><Box variant="awsui-key-label">Read</Box><Box variant="h2">{formatNumber(preview.readUsers)}</Box></div>
											<div><Box variant="awsui-key-label">Already queued</Box><Box variant="h2">{formatNumber(preview.queuedUsers)}</Box></div>
										</ColumnLayout>
									</Container>
									<Container header={<Header>Notification details</Header>}>
										<SpaceBetween size="m">
											<ColumnLayout columns={3} variant="text-grid">
												<div><Box variant="awsui-key-label">Local name</Box><div>{notification.localName || "-"}</div></div>
												<div><Box variant="awsui-key-label">Type</Box><Badge color={notification.type === "system" ? "blue" : "grey"}>{notification.type || "-"}</Badge></div>
												<div><Box variant="awsui-key-label">Status</Box><Badge color="green">Active</Badge></div>
												<div><Box variant="awsui-key-label">From</Box><div>{formatDateTime(notification.from)}</div></div>
												<div><Box variant="awsui-key-label">To</Box><div>{formatDateTime(notification.to)}</div></div>
											</ColumnLayout>
											<div><Box variant="awsui-key-label">Title</Box><Box variant="p">{notification.title || "-"}</Box></div>
											<div><Box variant="awsui-key-label">Description</Box><Box variant="p">{notification.description || "-"}</Box></div>
										</SpaceBetween>
									</Container>
								</SpaceBetween>
							),
						},
						{
							id: "cohorts",
							label: "Cohorts",
							content: <CohortConditionsEditor conditions={modal.cohortConditions} onChange={updateCohortConditions} onPreview={previewAudience} previewCount={preview.count} previewLoading={preview.loading} previewError={preview.error} />,
						},
					]}
				/>
			) : <Alert type="error">Notification data is missing.</Alert>}
		</Modal>
	);
}

export default NotificationRepeatModal;
